import { useState, useCallback, useContext, useEffect } from 'react';
import { ModalDialog, ButtonBar, FlowForm } from 't-lib-ui/structure';
import { TextButton } from 't-lib-ui/widgets';
import { AppState } from '../context/general';
import { MAX_UNREAD_CHOICES, MAX_AGE_DAYS_CHOICES } from '../schema-copy/readmarker-helper';
import DiscreteSlider, { SliderStop } from './DiscreteSlider';
import './LimitUnreadPopup.css';

export const MAX_UNREAD_STOPS: SliderStop[] = [
  { value: 0, label: 'Off' },
  ...MAX_UNREAD_CHOICES.map(v => ({ value: v, label: `${v}` })),
];

export const MAX_AGE_STOPS: SliderStop[] = [
  { value: 0, label: 'Off' },
  ...MAX_AGE_DAYS_CHOICES.map(v => ({ value: v, label: v == 1 ? '24h' : `${v}d` })),
];

/**
 * Preview line for the limit sliders; wouldMark comes from FeedEngine.previewAutoRead.
 * Phrased around the resulting unread count: the card applies limits immediately, so once
 * enforcement marks the posts, "Marks 37 posts as read, keeping 10 unread." becomes
 * "All 10 unread posts are within these limits." — the number carries over.
 */
export function limitPreviewText(maxUnread: number, maxAgeDays: number, wouldMark: number | undefined, unreadCount: number): string {
  if (!maxUnread && !maxAgeDays) return 'No limits set - posts stay unread until you read them.';
  if (wouldMark == undefined) return 'Calculating…';
  if (wouldMark) {
    const remaining = unreadCount - wouldMark;
    return `Marks ${wouldMark} ${wouldMark == 1 ? 'post' : 'posts'} as read, keeping ${remaining} unread.`;
  }
  if (!unreadCount) return 'All posts are read.';
  if (unreadCount == 1) return 'The unread post is within these limits.';
  return `All ${unreadCount} unread posts are within these limits.`;
}

export function useLimitUnread(): { openLimitUnread: (feedId: string) => void; limitUnreadPopup: React.ReactNode } {
  const [feedId, setFeedId] = useState<string | null>(null);

  const openLimitUnread = useCallback((id: string) => setFeedId(id), []);

  const limitUnreadPopup = (
    <LimitUnreadPopup
      show={!!feedId}
      feedId={feedId}
      onClose={() => setFeedId(null)}
    />
  );

  return { openLimitUnread, limitUnreadPopup };
}

interface LimitUnreadPopupProps {
  show: boolean;
  feedId: string | null;
  onClose: () => void;
}

const LimitUnreadPopup: React.FC<LimitUnreadPopupProps> = ({ show, feedId, onClose }) => {
  const appState = useContext(AppState);
  const feedEngine = appState.feedEngine;

  const [maxUnread, setMaxUnread] = useState(0);
  const [maxAgeDays, setMaxAgeDays] = useState(0);
  // Bumped when the post times arrive so the preview recomputes.
  const [, setSemisLoaded] = useState(0);

  // (Re)initialize from the feed's current limits whenever the dialog opens,
  // and fetch the post times needed for the live preview.
  useEffect(() => {
    if (!show || !feedId) return;
    const limits = feedEngine.getFeedLimits(feedId);
    setMaxUnread(limits.maxUnread ?? 0);
    setMaxAgeDays(limits.maxAgeDays ?? 0);
    feedEngine.ensureFeedSemis(feedId).then(() => setSemisLoaded(n => n + 1));
  }, [show, feedId, feedEngine]);

  const handleApply = useCallback(() => {
    if (feedId) feedEngine.setFeedLimits(feedId, maxUnread, maxAgeDays);
    onClose();
  }, [feedId, feedEngine, maxUnread, maxAgeDays, onClose]);

  const preview = feedId ? feedEngine.previewAutoRead(feedId, maxUnread, maxAgeDays) : 0;

  return (
    <ModalDialog
      show={show}
      title="Limit unread posts"
      closeButton="close"
      onClose={onClose}
      className="limitUnreadPopup"
    >
      <p className="limitUnreadNote">
        Posts beyond these limits are automatically marked as read. This means older posts quietly
        drop out of your unread count instead of piling up.
      </p>
      <FlowForm>
        <DiscreteSlider label="Maximum number of posts" stops={MAX_UNREAD_STOPS} value={maxUnread} onChange={setMaxUnread} />
        <DiscreteSlider label="Maximum age" stops={MAX_AGE_STOPS} value={maxAgeDays} onChange={setMaxAgeDays} />
        <p className="limitUnreadPreview">{limitPreviewText(maxUnread, maxAgeDays, preview, feedId ? feedEngine.getUnreadCount(feedId) : 0)}</p>
        <ButtonBar className="full-margin-top" align="right">
          <TextButton type="minor" onClick={onClose} label="Cancel" />
          <TextButton type="major" focussable="sideway" onClick={handleApply} label="Apply" />
        </ButtonBar>
      </FlowForm>
    </ModalDialog>
  );
};

export default LimitUnreadPopup;
